import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import CreateEventModal from '../components/CreateEventModal';
import EventCard from '../components/EventCard';
import { S } from '../components/styles';

export default function Dashboard({ events, loading, onNavigate, onCreate, profile }) {
  const [showCreate, setShowCreate] = useState(false);
  const upcoming = events.filter(e => e.status === 'upcoming');
  const past = events.filter(e => e.status === 'past');
  const myDishes = events.flatMap(e => (e.dishes||[]).filter(d => d.guest_id === profile?.id));
  const pendingApprovals = events.filter(e => e.host_id === profile?.id).flatMap(e => (e.dishes||[]).filter(d => d.status === 'pending'));
  const allRatedDishes = past.flatMap(e => (e.dishes||[]).filter(d => d.guest_id === profile?.id && d.dish_ratings?.length > 0));
  
  const myAvg = allRatedDishes.length ? allRatedDishes.reduce((a,d) => a + (d.dish_ratings.reduce((x,r)=>x+r.rating,0)/d.dish_ratings.length), 0) / allRatedDishes.length : 0;

const firstPendingEvent = events.find(e =>
  e.host_id === profile?.id && (e.dishes||[]).some(d => d.status === 'pending')
);

const metrics = [
  {
    icon: '📅',
    val: upcoming.length,
    label: 'Upcoming events',
    onClick: () => onNavigate('events'),
  },
  {
    icon: '🍳',
    val: myDishes.length,
    label: "Dishes you're bringing",
    onClick: () => {
      const firstEvent = events.find(e => (e.dishes||[]).some(d => d.guest_id === profile?.id));
      if (firstEvent) onNavigate('event', firstEvent.id);
    },
  },
  {
    icon: '⏳',
    val: pendingApprovals.length,
    label: 'Pending your approval',
    onClick: () => { if (firstPendingEvent) onNavigate('event', firstPendingEvent.id); },
  },
  {
    icon: '⭐',
    val: myAvg > 0 ? myAvg.toFixed(1) + '★' : '—',
    label: 'Your dishes avg rating',
    onClick: () => onNavigate('hits'),
  },
];

  return (
    <div>
      <div style={{...S.pageHero, display:'flex', alignItems:'flex-start', justifyContent:'space-between', flexWrap:'wrap', gap:16}}>
        <div>
          <p style={S.textMuted}>Good evening</p>
          <h2 style={S.h2}>Welcome back, {profile?.full_name?.split(' ')[0] || 'friend'} 👋</h2>
        </div>
        <button style={S.btnPrimary} onClick={() => setShowCreate(true)}>+ Host a dinner</button>
      </div>

      <div style={S.content}>
        {loading && <p style={{color:'#7A6555',fontSize:14}}>Loading your events…</p>}

        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(180px,1fr))',gap:14,marginBottom:28}}>
  {metrics.map(s => {
    const isClickable = !!(
      s.onClick &&
      (s.label === 'Upcoming events'        ? upcoming.length > 0 :
       s.label === "Dishes you're bringing" ? myDishes.length > 0 :
       s.label === 'Pending your approval'  ? pendingApprovals.length > 0 :
       s.label === 'Your dishes avg rating' ? myAvg > 0 : true)
    );
    return (
      <div
        key={s.label}
        onClick={isClickable ? s.onClick : undefined}
        style={{
          ...S.metricCard,
          cursor: isClickable ? 'pointer' : 'default',
          transition: 'all .15s',
        }}
        onMouseEnter={e => {
          if (isClickable) {
            e.currentTarget.style.boxShadow = '0 4px 16px rgba(44,26,14,.1)';
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.borderColor = '#C4612A';
          }
        }}
        onMouseLeave={e => {
          e.currentTarget.style.boxShadow = 'none';
          e.currentTarget.style.transform = 'none';
          e.currentTarget.style.borderColor = '#E5D9C8';
        }}
      >
        <div style={{fontSize:22,marginBottom:10}}>{s.icon}</div>
        <div style={{fontFamily:'Cormorant Garamond,serif',fontSize:30,color:'#2C1A0E'}}>{s.val}</div>
        <div style={{fontSize:12,color:'#7A6555',marginTop:4}}>{s.label}</div>
        {isClickable && (
          <div style={{fontSize:11,color:'#C4612A',marginTop:6,fontWeight:500}}>View →</div>
        )}
      </div>
    );
  })}
</div>

        {pendingApprovals.length > 0 && (
          <div style={{marginBottom:28}}>
            <h3 style={{...S.h3,marginBottom:14}}>Needs your approval 🕐</h3>
            {pendingApprovals.map(d => {
              const ev = events.find(e => (e.dishes||[]).find(x => x.id === d.id));
              return (
                <div key={d.id} style={{...S.card,...S.cardPad,display:'flex',alignItems:'center',gap:12,marginBottom:10}}>
                  <div style={{width:8,height:8,borderRadius:'50%',background:'#C4612A',flexShrink:0}}/>
                  <div style={{flex:1}}>
                    <p style={{fontWeight:500}}>{d.name}</p>
                    <p style={{fontSize:12,color:'#7A6555'}}>{d.profiles?.full_name} · {d.category} · {d.method}</p>
                  </div>
                  <button style={S.btnPrimary} onClick={() => onNavigate('event', ev?.id)}>Review →</button>
                </div>
              );
            })}
          </div>
        )}

        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:14}}>
          <h3 style={S.h3}>Your upcoming gatherings</h3>
        </div>
        {upcoming.length === 0 && !loading && (
          <div style={S.empty}>
            <div style={{fontSize:52,marginBottom:16}}>🍽️</div>
            <h3 style={{fontFamily:'Cormorant Garamond,serif',fontSize:24,marginBottom:8}}>No upcoming events</h3>
            <p style={{color:'#7A6555',fontSize:14,marginBottom:22}}>Host your first dinner party or wait for an invitation!</p>
            <button style={S.btnPrimary} onClick={() => setShowCreate(true)}>Host a dinner</button>
          </div>
        )}
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(300px,1fr))',gap:18,marginBottom:32}}>
          {upcoming.map(ev => <EventCard key={ev.id} event={ev} onClick={() => onNavigate('event', ev.id)} currentUserId={profile?.id}/>)}
        </div>

        {past.length > 0 && (
          <>
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:14}}>
              <h3 style={S.h3}>Recent gatherings</h3>
              <button style={S.btnGhost} onClick={() => onNavigate('history')}>View all →</button>
            </div>
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(300px,1fr))',gap:18}}>
              {past.slice(0,2).map(ev => <EventCard key={ev.id} event={ev} onClick={() => onNavigate('event', ev.id)} currentUserId={profile?.id}/>)}
            </div>
          </>
        )}
      </div>

      {showCreate && <CreateEventModal onClose={() => setShowCreate(false)} onCreate={async form => { await onCreate(form); setShowCreate(false); }}/>}
    </div>
  );
}
